import React from "react";
import { CommandEmpty, CommandGroup, CommandList } from "cmdk";
import * as ScrollArea from "@radix-ui/react-scroll-area";

import { FeatureFlagNames, useFeatureFlags } from "@/providers/featureFlags";
import { NotFound } from "@/components/common/NotFound";
import { SearchItemAlert } from "@/components/common/Search/SearchItemAlert";
import { SearchItemAsset } from "@/components/common/Search/SearchItemAsset";
import { SearchItemDevice } from "@/components/common/Search/SearchItemDevice";
import { SearchItemReader } from "@/components/common/Search/SearchItemReader";
import { SearchItemTrip } from "@/components/common/Search/SearchItemTrip";
import { SearchableEntity } from "@/components/common/Search/types";
import { Scrollbar } from "@/components/ui/Scrollbar";
import { Spinner } from "@/components/ui/Spinner";

import { useStandbyDeviceQuery } from "@/features/system/queries/useStandbyDeviceQuery";

import cn from "@/utils/cn";
import { SearchResponse } from "@/utils/remote/types";

type Props = {
  searchResponse?: SearchResponse;
  entity: SearchableEntity;
  query: string;
  onItemSelected: (to: string) => void;
} & React.HTMLAttributes<typeof CommandGroup>;

// SearchResults must be put inside a root Command component from "cmdk"
// That allows connecting it with a search input for better keyboard support
export const SearchResults = ({
  searchResponse,
  entity,
  query,
  onItemSelected,
}: Props) => {
  const { isEnabled, loading: featureFlagsLoading } = useFeatureFlags();
  const showStandbySensors = isEnabled(FeatureFlagNames.ShowStandBySensors);
  const trimmedQuery = query.trim();
  const noDeviceMatches =
    entity.key === "device" &&
    trimmedQuery.length > 0 &&
    searchResponse !== undefined &&
    !searchResponse.devices?.length;

  const standbyLookupAllowed =
    noDeviceMatches && !featureFlagsLoading && showStandbySensors;
  const { isFetching: standbyLookupFetching, isSuccess: standbyDeviceFound } =
    useStandbyDeviceQuery(trimmedQuery, standbyLookupAllowed);

  const buildElements = (
    response: SearchResponse | undefined,
    entity: SearchableEntity,
  ) => {
    if (!response) return null;

    switch (entity.key) {
      case "alert":
        return response?.alerts?.map((alert) => {
          return (
            <SearchItemAlert
              key={alert.chorusAlertId}
              alert={alert}
              onSelect={onItemSelected}
              query={query}
            />
          );
        });
      case "asset":
        return response?.assets?.map((asset) => {
          return (
            <SearchItemAsset
              key={asset.assetId.chorusAssetId}
              asset={asset}
              onSelect={onItemSelected}
              query={query}
            />
          );
        });
      case "device":
        return response?.devices?.map((device) => {
          return (
            <SearchItemDevice
              key={device.deviceId}
              device={device}
              onSelect={onItemSelected}
              query={query}
            />
          );
        });
      case "trip":
        return response?.trips?.map((trip) => {
          return (
            <SearchItemTrip
              key={trip.tripId.chorusTripId}
              trip={trip}
              onSelect={onItemSelected}
              query={query}
            />
          );
        });
      case "reader":
        return response?.readers?.map((reader) => {
          return (
            <SearchItemReader
              key={reader.readerId}
              reader={reader}
              onSelect={onItemSelected}
              query={query}
            />
          );
        });
    }
  };
  const elements = buildElements(searchResponse, entity);

  const emptyState = () => {
    if (standbyLookupAllowed && standbyLookupFetching) {
      return (
        <div
          className="flex justify-center py-10"
          data-testid="search-standby-lookup-loading"
        >
          <Spinner size="md" />
        </div>
      );
    }
    if (standbyLookupAllowed && standbyDeviceFound) {
      return <NotFound query={query} varient="notActivated" />;
    }
    return <NotFound query={query} />;
  };

  return (
    <>
      <CommandList
        data-testid="search-results"
        className="w-full pr-[3px] md:absolute md:inset-x-0 md:z-[120] md:mt-2 md:max-w-[444px] md:rounded-lg md:bg-white  md:shadow-box-2 md:group-data-[state=open]:animate-in md:group-data-[state=closed]:animate-out md:group-data-[state=closed]:fade-out-0 md:group-data-[state=open]:fade-in-0 md:group-data-[state=closed]:zoom-out-95 md:group-data-[state=open]:zoom-in-95"
      >
        <ScrollArea.Root
          type="always"
          className={cn("h-full w-full overflow-hidden")}
        >
          <ScrollArea.Viewport
            className={cn("w-full rounded-lg md:max-h-[336px] [&>div]:!block")}
          >
            {elements?.length && elements?.length > 0 ? (
              elements
            ) : (
              <CommandEmpty className="mt-[92px] md:mb-[24px] md:mt-[20px]">
                {emptyState()}
              </CommandEmpty>
            )}
          </ScrollArea.Viewport>
          {elements?.length && elements?.length > 0 ? <Scrollbar /> : null}
        </ScrollArea.Root>
      </CommandList>
    </>
  );
};
